<template>
  <!-- DEMO COMPONENT (Oct 2026) -- treat as throwaway: sites list on the left, the selected site on the right (CompanySiteDetail) -->
  <div class="company-sites-manager d-flex">
    <!-- LEFT: list of sites -->
    <div class="company-sites-manager__sidebar d-flex flex-column">
      <div class="d-flex align-items-end justify-content-between py-2">
        <span class="font-weight-bold font-size-110">{{ $t('companySitesManager.sites') }}</span>
        <b-button
          variant="soft-success"
          size="sm"
          @click="onAddSite"
        >
          <b-icon
            icon="plus"
            class="mr-1"
          ></b-icon>
          {{ $t('companySitesManager.addSite') }}
        </b-button>
      </div>
      <b-overlay
        :show="loadingSites"
        class="flex-fill overflow-auto bg-white border rounded"
      >
        <div class="company-sites-manager__site-list d-flex flex-column">
          <div
            v-for="site in sites"
            :key="site.id"
            class="company-sites-manager__site-item"
            :class="{ 'active': site.id === selectedSiteId, 'has-you-badge': hasReviewForMe(site) }"
            role="button"
            tabindex="0"
            @click="selectSite(site.id)"
          >
            <!-- a review (new site / supplier change request) waiting for the current user -- same look as the 'You' task badge in FlowObjectDetail -->
            <div
              v-if="hasReviewForMe(site)"
              class="is-assigned-tu-badge special-task-assignee-badge shadow-sm"
              :title="$t('companySitesManager.reviewWaiting')"
            >{{ $t('companySitesManager.you') }}</div>
            <div class="d-flex align-items-center">
              <span class="font-weight-600">{{ site.display_name }}</span>
              <!-- Oct 2026 site states + review tasks -->
              <b-badge
                v-if="site.sm_state && site.sm_state !== 'active'"
                variant="soft-secondary"
                class="ml-2 text-uppercase"
              >{{ site.sm_state }}</b-badge>
              <b-icon
                v-if="site.is_primary"
                icon="star-fill"
                class="ml-auto"
                :title="$t('companySitesManager.primarySite')"
              ></b-icon>
            </div>
            <div
              v-if="site.address && site.address.formatted_city_state_country"
              class="font-size-80 opacity-75"
            >
              {{ site.address.formatted_city_state_country }}
            </div>
            <div
              v-if="site.site_types.length"
              class="mt-1 d-flex flex-wrap gap-2"
            >
              <company-site-type-badge
                v-for="siteType in site.site_types"
                :key="siteType.id"
                :site-type="siteType"
                small
              ></company-site-type-badge>
            </div>
          </div>
        </div>
        <div
          v-if="!loadingSites && sites.length === 0"
          class="p-3 text-muted"
        >
          {{ $t('companySitesManager.noSites') }}
        </div>
      </b-overlay>
    </div>

    <!-- RIGHT: the selected site -- :key gives every site a fresh CompanySiteDetail (no form state carried between sites) -->
    <div class="flex-fill overflow-auto px-4 pb-4 d-flex flex-column">
      <company-site-detail
        v-if="selectedSiteId"
        :key="selectedSiteId"
        :site-id="selectedSiteId"
        @saved="onSiteSaved"
        @deleted="onSiteDeleted"
      ></company-site-detail>
      <div
        v-else
        class="text-muted"
      >
        {{ sites.length ? $t('companySitesManager.selectASite') : '' }}
      </div>
    </div>

    <!-- create a new site with the existing modal -->
    <company-site-modal
      ref="siteModal"
      :company-id="companyId"
      @saved="onSiteCreated"
    ></company-site-modal>
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { errorHandling } from '@/js/custom-vue-mixins';
  import CompanySiteModal from '@/components/vendors/components/CompanySiteModal.vue';
  import CompanySiteDetail from '@/components/vendors/components/CompanySiteDetail.vue';
  import CompanySiteTypeBadge from '@/components/vendors/components/CompanySiteTypeBadge.vue';

  // DEMO COMPONENT (Oct 2026) -- treat as throwaway
  // all CompanySites of one company: the list on the left picks which site CompanySiteDetail shows (and edits) on the right
  // ONLY for tenants with custom_flags.use_vendor_sites -- emits 'changed' after anything is saved, created or deleted
  export default {
    name: 'CompanySitesManager',
    components: {
      CompanySiteModal,
      CompanySiteDetail,
      CompanySiteTypeBadge,
    },
    mixins: [errorHandling],
    i18n: {
      // English only -- throwaway demo component, other languages fall back to English
      messages: {
        en: {
          companySitesManager: {
            sites: 'Sites',
            addSite: 'Add Site',
            noSites: 'No sites yet.',
            selectASite: 'Select a site on the left to see and edit it.',
            primarySite: 'Primary site',
            errorLoading: 'Error while loading sites',
            reviewWaiting: 'Waiting for your review',
            you: 'You',
          },
        },
      },
    },
    props: {
      companyId: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        loadingSites: false,
        sites: [],
        selectedSiteId: null,
      };
    },
    async mounted() {
      await this.reloadSites();
      this.selectDefaultSite();
    },
    methods: {
      hasReviewForMe(site) {
        // an open site review task (new site / supplier change request) assigned to the current user -- CompanySiteReadSerializer.my_review_tasks
        return !!(site.my_review_tasks && site.my_review_tasks.length);
      },
      async reloadSites() {
        this.loadingSites = true;
        try {
          const response = await suppliosApi.companySite.list({ params: { company: this.companyId } });
          this.sites = response.data;
        } catch (error) {
          this.errorToaster(error, this.$t('companySitesManager.errorLoading'));
        } finally {
          this.loadingSites = false;
        }
      },
      selectDefaultSite() {
        const site = this.sites.find((s) => s.is_primary) || this.sites[0];
        this.selectedSiteId = site ? site.id : null;
      },
      selectSite(siteId) {
        this.selectedSiteId = siteId;
      },
      onAddSite() {
        this.$refs.siteModal.startCreate();
      },
      async onSiteCreated(site) {
        await this.reloadSites();
        this.selectSite(site.id);
        this.$emit('changed');
      },
      async onSiteSaved() {
        // the list shows name / city / types / primary star -- and making one site primary changes another
        await this.reloadSites();
        this.$emit('changed');
      },
      async onSiteDeleted() {
        this.selectedSiteId = null;
        await this.reloadSites();
        this.selectDefaultSite();
        this.$emit('changed');
      },
    },
  };
</script>

<style scoped>
  .company-sites-manager {
    min-height: 600px;
  }
  .company-sites-manager__sidebar {
    width: 320px;
    flex-shrink: 0;
  }
  .company-sites-manager__site-item {
    position: relative; /* for the 'You' badge */
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-left: 5px solid transparent;
    border-bottom: 1px solid #e9ecef;
  }
  .company-sites-manager__site-item:hover {
    background-color: var(--gray-50);
  }
  .company-sites-manager__site-item.active {
    background-color: var(--primary-50);
    border-left-color: var(--primary-500);
  }
  .company-sites-manager__site-item.has-you-badge {
    padding-top: 1.25rem; /* room for the 'You' badge above the name */
  }
  /* 'You' badge -- copied from the task list in FlowObjectDetail (.special-task-assignee-badge / .is-assigned-tu-badge), which is nested there */
  .special-task-assignee-badge {
    position: absolute;
    top: 4px;
    left: -2px;
    padding: 2px 4px 1px 4px;
    font-weight: 700;
    font-size: 0.6rem;
    text-transform: uppercase;
    border-width: 1px 1px 1px 0;
    border-style: solid;
    border-color: transparent;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  .is-assigned-tu-badge {
    color: var(--danger-600);
    background-color: var(--danger-100);
  }
  .company-sites-manager__kind-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
  }
</style>
